/** @jsx jsx */
import type { Interpolation } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import classNames from 'classnames';
import URI from 'jsuri';

import type { Theme } from '@coursera/cds-core';
import { Button } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { TrackedA } from 'bundles/page/components/TrackedLink2';

const VARIANTS = {
  SKINNY: 'skinny',
} as const;

const styles: Record<string, Interpolation<Theme>> = {
  enterpriseLink: css`
    margin-bottom: 24px;

    @media screen and (max-width: 767px) {
      display: block;
      width: 100%;
    }
  `,
  c4bButton: () => css`
    align-items: center;
    border: 2px solid var(--cds-color-blue-700);
    display: inline-flex;
    height: 72px;
    margin-left: 0;
    text-decoration: none;
    vertical-align: top;
    width: 320px;

    &:hover {
      text-decoration: none;

      span {
        text-decoration: none;
      }
    }

    span {
      display: flex;
      height: 24px;
      justify-content: center;
      line-height: 24px;
      text-decoration: none;
      width: 100%;
    }

    @media screen and (max-width: 1350px) {
      display: inline-block;
      width: auto;
      height: auto;
    }

    @media screen and (max-width: 768px) {
      margin-top: -18px;
      margin-bottom: 36px;
      width: 100%;
    }
  `,
  skinny: () => css`
    padding-top: 6px;
    padding-bottom: 6px;
    border: 2px solid var(--cds-color-grey-300);
    color: var(--cds-color-grey-975);
    font-weight: 600;
    border-radius: 8px;
    box-shadow: unset;
    max-width: unset;

    :hover {
      box-shadow: unset;
      border-color: var(--cds-color-blue-700);
    }

    :focus {
      box-shadow: unset;
    }
  `,
};

const enterpriseUriQueryString =
  '?utm_campaign=website&utm_content=banner-from-B2C-home-try-business&utm_medium=coursera&utm_source=enterprise';
const demoLinkWithUtmParams = new URI('/business').setQuery(enterpriseUriQueryString).toString();

type Props = {
  label: string;
  link?: string | null;
  useExtraCtaStyling?: boolean;
  className?: string;
  variant?: (typeof VARIANTS)[keyof typeof VARIANTS];
};

const CourseraForBusinessCTAComponent = ({ label, link, className, useExtraCtaStyling, variant }: Props) => {
  const trackEvent = useTracker();

  const linkFromPropsOrDemoLink = link ?? demoLinkWithUtmParams;

  const handleCTAClick = () => {
    trackEvent('click_button', {
      button: {
        name: 'hero_secondary_cta',
        linkName: label,
        linkURL: linkFromPropsOrDemoLink,
        linkType: 'internal',
      },
      pageSection: { sectionName: 'hero_banner' },
    });
  };

  const btnClassname = classNames('enterprise-cta-double-blue', className);

  return (
    <Button
      component={TrackedA}
      data-unit="c4b-cta"
      trackingName="enterprise_cta_double_blue"
      withVisibilityTracking={false}
      href={linkFromPropsOrDemoLink}
      className={btnClassname}
      css={[styles.linkButton, useExtraCtaStyling && styles.c4bButton, variant === VARIANTS.SKINNY && styles.skinny]}
      variant="secondary"
      role="link"
      onClick={handleCTAClick}
    >
      {label}
    </Button>
  );
};

export default CourseraForBusinessCTAComponent;
